import { useState } from 'react';

// ── PALETTE (copia locale — identica a App.js) ────────────────────────────────
const P = {
  bg:"#080B14", card:"#0D1020", border:"#1D2238",
  gold:"#E9622A", goldGlow:"rgba(233,98,42,0.12)", goldBorder:"rgba(233,98,42,0.30)",
  text:"#EBE8F5", muted:"#8A8EAE", secondary:"#B4B7D0",
  green:"#5BB87A", yellow:"#E9622A", red:"#C46B6B", blue:"#5A82B8", purple:"#a78bfa",
  sidebar:"#060810", card2:"#111525", card3:"#161B2E",
  borderSubtle:"#141829", borderStrong:"#2A3055"
};

const MESI_IT = [
  "Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno",
  "Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"
];

// "YYYY-MM" → "Aprile 2026"
const labelMese = (tag) => {
  const [y, m] = tag.split('-');
  return `${MESI_IT[parseInt(m, 10) - 1]} ${y}`;
};

// oggi → "YYYY-MM"
const tagOggi = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

// "YYYY-MM" + delta (-1 / +1) → "YYYY-MM"
const shiftMese = (tag, delta) => {
  const [y, m] = tag.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

// primo giorno del mese → "YYYY-MM-DD"
const primoGiorno = (tag) => `${tag}-01`;

// "15,50" / "15.5" / "€ 1.500,50" → numero. Prima la virgola italiana dava 0.
const leggiImporto = (s) => {
  let t = String(s ?? '').replace(/[€\s]/g, '');
  if (t.includes(',')) t = t.replace(/\./g, '').replace(',', '.');
  const n = parseFloat(t);
  return isNaN(n) ? 0 : Math.round(n * 100) / 100;
};

// 15.5 → "15,50", 1500 → "1500" (senza separatore delle migliaia, così si rilegge uguale)
const fmtImporto = (n) => (Number(n) || 0).toLocaleString('it-IT', {
  minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2, useGrouping: false
});

export default function SalaProve({ sala, isMobile, addSala, deleteSala, updateSalaCell, notify }) {
  const [meseSala, setMeseSala]       = useState(tagOggi);
  const [nuovoId, setNuovoId]         = useState(null); // riga appena creata: il nome prende il cursore
  const [daEliminare, setDaEliminare] = useState(null); // riga in attesa di conferma eliminazione

  // ── filtro per mese, in ordine di data ──────────────────────────────────────
  const salaMese = sala
    .filter(r => r.data && r.data.startsWith(meseSala))
    .sort((a, b) => a.data.localeCompare(b.data) || (Number(a.id) - Number(b.id)));

  // ── navigazione ─────────────────────────────────────────────────────────────
  const prev = () => setMeseSala(t => shiftMese(t, -1));
  const next = () => setMeseSala(t => shiftMese(t, +1));

  // ── salvataggio ─────────────────────────────────────────────────────────────
  // I campi sono input sempre presenti (non ricreati a ogni tasto): si salva uscendo
  // dal campo o con Invio, Esc rimette il valore di prima. Si scrive solo se cambia.
  const salva = (r, field, raw) => {
    let val = raw;
    if (field === 'pagato' || field === 'bibite') val = leggiImporto(raw);
    if (field === 'nome') val = String(raw).trim();
    // Senza data la prenotazione sparirebbe da tutti i mesi
    if (field === 'data' && !raw) return;
    if (val === r[field]) return;
    updateSalaCell(r.id, field, val);
    if (field === 'data' && !val.startsWith(meseSala)) {
      notify(`Prenotazione spostata a ${labelMese(val.slice(0, 7))}`, 'ok');
    }
  };

  const tasti = (e, valorePrima) => {
    if (e.key === 'Enter') e.currentTarget.blur();
    if (e.key === 'Escape') { e.currentTarget.value = valorePrima; e.currentTarget.blur(); }
  };

  const nuovaPrenotazione = async () => {
    // oggi se siamo nel mese corrente, altrimenti il primo del mese mostrato
    const id = await addSala(meseSala === tagOggi() ? undefined : primoGiorno(meseSala));
    if (id) setNuovoId(id);
  };

  // ── totali mese ──────────────────────────────────────────────────────────────
  const totPagato = salaMese.reduce((s, r) => s + (Number(r.pagato) || 0), 0);
  const totBibite = salaMese.reduce((s, r) => s + (Number(r.bibite) || 0), 0);

  // ── campi ───────────────────────────────────────────────────────────────────
  // La key include il valore: se cambia da fuori (salvataggio, errore) il campo si aggiorna
  const campoNome = (r) => (
    <input key={`${r.id}-nome-${r.nome}`} className="sala-input" defaultValue={r.nome}
      placeholder="Nome o gruppo" autoFocus={r.id === nuovoId}
      onFocus={() => { if (r.id === nuovoId) setNuovoId(null); }}
      onBlur={e => salva(r, 'nome', e.target.value)}
      onKeyDown={e => tasti(e, r.nome)}
      style={{ fontWeight: 600 }} />
  );

  const campoData = (r) => (
    <input key={`${r.id}-data-${r.data}`} type="date" className="sala-input" defaultValue={r.data}
      onBlur={e => salva(r, 'data', e.target.value)}
      onKeyDown={e => tasti(e, r.data)}
      style={{ colorScheme: 'dark', minWidth: 130 }} />
  );

  const campoImporto = (r, field) => {
    const testo = r[field] ? fmtImporto(r[field]) : '';
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <span style={{ color: P.muted, fontFamily: "'IBM Plex Mono',monospace" }}>€</span>
        <input key={`${r.id}-${field}-${r[field]}`} className="sala-input" inputMode="decimal"
          defaultValue={testo} placeholder="0"
          onFocus={e => e.target.select()}
          onBlur={e => salva(r, field, e.target.value)}
          onKeyDown={e => tasti(e, testo)}
          style={{ fontFamily: "'IBM Plex Mono',monospace", minWidth: 60,
            color: field === 'pagato' ? P.gold : P.text }} />
      </div>
    );
  };

  const totaleRiga = (r) => {
    const t = (Number(r.pagato) || 0) + (Number(r.bibite) || 0);
    return (
      <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontWeight: 600, color: t > 0 ? P.green : P.muted }}>
        {t > 0 ? `€${fmtImporto(t)}` : '—'}
      </span>
    );
  };

  const elimina = (r) => daEliminare === r.id ? (
    <span style={{ display: 'inline-flex', gap: 6 }}>
      <button onClick={() => { setDaEliminare(null); deleteSala(r.id); }}
        style={{ padding: '6px 10px', borderRadius: 6, border: `1px solid ${P.red}60`, background: `${P.red}20`,
          color: P.red, cursor: 'pointer', fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap' }}>Elimina</button>
      <button onClick={() => setDaEliminare(null)}
        style={{ padding: '6px 10px', borderRadius: 6, border: `1px solid ${P.border}`, background: 'transparent',
          color: P.secondary, cursor: 'pointer', fontSize: 14 }}>No</button>
    </span>
  ) : (
    <button onClick={() => setDaEliminare(r.id)} title="Elimina prenotazione"
      style={{ background: 'transparent', border: `1px solid transparent`, color: P.muted,
        cursor: 'pointer', fontSize: 17, padding: '6px 10px', borderRadius: 6, lineHeight: 1 }}>✕</button>
  );

  const etichetta = (t) => (
    <div style={{ fontSize: 12, color: P.muted, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 2 }}>{t}</div>
  );

  const navBtn = {
    background: P.card, border: `1px solid ${P.border}`, color: P.text, borderRadius: 8,
    width: 40, height: 40, cursor: "pointer", fontSize: 20, lineHeight: 1,
    display: "flex", alignItems: "center", justifyContent: "center",
  };

  return (
    <div style={{ animation: "fi .35s ease" }}>
      <style>{`
        .sala-input{width:100%;background:transparent;border:none;border-bottom:1px solid transparent;
          border-radius:0;color:${P.text};font-size:16px;padding:6px 2px;outline:none;font-family:'Figtree',sans-serif;}
        .sala-input:hover{border-bottom-color:${P.border};}
        .sala-input:focus{border-bottom-color:${P.gold};background:${P.gold}0D;}
        .sala-input::placeholder{color:${P.muted};font-weight:400;}
      `}</style>

      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20, gap:12, flexWrap:"wrap" }}>
        <div>
          <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:11, letterSpacing:3,
            color:P.muted, marginBottom:7, fontWeight:400, textTransform:"uppercase" }}>STRUTTURA</div>
          <h1 style={{ fontFamily:"'Syne',sans-serif", fontSize:isMobile?22:32,
            fontWeight:800, letterSpacing:-0.5 }}>Sala Prove & Studio</h1>
        </div>
        <button onClick={nuovaPrenotazione}
          style={{ background:P.gold, color:"#fff", border:"none",
            padding:"11px 20px", borderRadius:8, cursor:"pointer",
            fontWeight:600, fontSize:16, fontFamily:"'Figtree',sans-serif" }}>
          + Prenotazione
        </button>
      </div>

      {/* Navigazione mesi */}
      <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:20 }}>
        <button onClick={prev} style={navBtn}>‹</button>
        <div style={{ fontFamily:"'Syne',sans-serif", fontSize:19, fontWeight:700,
          color:P.text, minWidth:160, textAlign:"center" }}>
          {labelMese(meseSala)}
        </div>
        <button onClick={next} style={navBtn}>›</button>
      </div>

      {/* Totali mese */}
      <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr 1fr":"repeat(4,1fr)", gap:12, marginBottom:20 }}>
        {[
          { l:"Prenotazioni", v: salaMese.length,                         c: P.blue  },
          { l:"Pagato sala",  v: `€${fmtImporto(totPagato)}`,             c: P.gold  },
          { l:"Bibite",       v: `€${fmtImporto(totBibite)}`,             c: P.text  },
          { l:"Totale",       v: `€${fmtImporto(totPagato + totBibite)}`, c: P.green },
        ].map(s => (
          <div key={s.l} style={{ background:P.card, border:`1px solid ${P.border}`,
            borderRadius:12, padding:isMobile?"14px 16px":"18px 22px" }}>
            <div style={{ fontSize:14, color:P.secondary, marginBottom:6 }}>{s.l}</div>
            <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:isMobile?20:24,
              fontWeight:500, color:s.c }}>{s.v}</div>
          </div>
        ))}
      </div>

      {salaMese.length === 0 ? (
        <div style={{ background:P.card, border:`1px solid ${P.border}`, borderRadius:14,
          padding:"36px 16px", textAlign:"center", color:P.secondary, fontSize:16 }}>
          Nessuna prenotazione per {labelMese(meseSala)}.
          <div style={{ marginTop:12 }}>
            <button onClick={nuovaPrenotazione}
              style={{ background:"transparent", color:P.gold, border:`1px solid ${P.gold}`,
                padding:"9px 18px", borderRadius:8, cursor:"pointer", fontSize:15 }}>+ Aggiungi la prima</button>
          </div>
        </div>
      ) : isMobile ? (
        /* Telefono: una scheda per prenotazione */
        <div style={{ display:"grid", gap:10 }}>
          {salaMese.map(r => (
            <div key={r.id} style={{ background:P.card, border:`1px solid ${P.border}`, borderRadius:12, padding:"10px 14px" }}>
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <div style={{ flex:1, minWidth:0 }}>{campoNome(r)}</div>
                {elimina(r)}
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1.4fr 1fr 1fr", gap:10, marginTop:8 }}>
                <div>{etichetta("Data")}{campoData(r)}</div>
                <div>{etichetta("Pagato")}{campoImporto(r, 'pagato')}</div>
                <div>{etichetta("Bibite")}{campoImporto(r, 'bibite')}</div>
              </div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center",
                marginTop:8, paddingTop:8, borderTop:`1px solid ${P.borderSubtle}` }}>
                <span style={{ fontSize:14, color:P.secondary }}>Totale</span>
                {totaleRiga(r)}
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* Schermo grande: tabella */
        <div style={{ background:P.card, border:`1px solid ${P.border}`, borderRadius:14, overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse" }}>
            <thead>
              <tr style={{ borderBottom:`1px solid ${P.border}` }}>
                {["Nome","Data","Pagato","Bibite","Totale",""].map((h, i) => (
                  <th key={i} style={{ padding:"13px 16px", textAlign:"left",
                    fontFamily:"'IBM Plex Mono',monospace", fontSize:12, letterSpacing:2,
                    color:P.muted, fontWeight:400, textTransform:"uppercase" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {salaMese.map((r, i) => (
                <tr key={r.id}
                  style={{ borderBottom: i < salaMese.length - 1 ? `1px solid ${P.border}` : "none" }}
                  onMouseEnter={e => e.currentTarget.style.background = `${P.gold}07`}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  <td style={{ padding:"8px 16px", minWidth:200 }}>{campoNome(r)}</td>
                  <td style={{ padding:"8px 16px", width:170 }}>{campoData(r)}</td>
                  <td style={{ padding:"8px 16px", width:130 }}>{campoImporto(r, 'pagato')}</td>
                  <td style={{ padding:"8px 16px", width:130 }}>{campoImporto(r, 'bibite')}</td>
                  <td style={{ padding:"8px 16px", width:110 }}>{totaleRiga(r)}</td>
                  <td style={{ padding:"8px 12px", textAlign:"right", width:150 }}>{elimina(r)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
